import { BaseElementController } from "elements/base/controller";
import type { TaskCard } from "shared/datamodel/schemas/task-card";
import type { IGanttController } from "../controller";
import { cleanDate, getRowColor } from "../utils";
import { IGanttBaseCellController } from "elements/gantt/controllers/base-cell-controller";
import { TaskColor } from "elements/gantt/constants";
import { TaskElement } from "elements/task/schema";

export default class GanttTaskCellController
  extends BaseElementController<TaskCard | TaskElement>
  implements IGanttBaseCellController<TaskCard | TaskElement>
{
  #ganttController: WeakRef<IGanttController> | undefined;

  updateElement(element: TaskCard): void {
    super.updateElement(element);
    this.notify();
  }

  getGanttLayoutRect(): {
    x: number;
    y: number;
    width: number;
    height: number;
  } {
    return this.#ganttController?.deref()?.getLayoutRect() ?? { x: 0, y: 0, width: 0, height: 0 };
  }

  updateController(ganttController: IGanttController): void {
    this.#ganttController = new WeakRef(ganttController);
    this.notify();
  }

  getLayout() {
    return this.#ganttController?.deref()?.getTaskCellLayout(this.id);
  }

  getTitle() {
    return this.element.title;
  }

  getBackgroundColor() {
    const elementKey = this.element.type === "task" ? "fill" : "color";

    if (this.#ganttController?.deref()?.element.customRows) {
      // @ts-expect-error - we know that the element is a task or taskCard
      return this.element[elementKey] as TaskColor;
    }

    // @ts-expect-error - we know that the element is a task or taskCard
    return getRowColor(this.element[elementKey]);
  }

  getGanttStartDate() {
    const cell = this.#ganttController?.deref()?.getDateColumnsLayout()[0];
    return new Date(cell?.id.split("cell-")[1] ?? 0);
  }

  getGanttEndDate() {
    const cell = this.#ganttController
      ?.deref()
      ?.getDateColumnsLayout()
      .filter((cell) => !cell.isHeader)
      .at(-1);

    return new Date(cell?.id.split("cell-")[1] ?? 0);
  }

  getStartDate() {
    const now = cleanDate(new Date()).getTime();
    return cleanDate(Math.min(this.element.fromDate || now, this.element.toDate ?? now));
  }

  getEndDate() {
    const now = cleanDate(new Date()).getTime();
    const startDate =
      this.element.type === "task" ? this.element.fromDate : this.element.fromDate ?? this.element.dueDate;
    return cleanDate(Math.max(startDate || now, this.element.toDate || now));
  }

  changeTitle(newTitle: string) {
    this.#ganttController?.deref()?.changeTaskTitle(this.id, newTitle);
  }

  isSelected() {
    return this.#ganttController?.deref()?.context.selectedElementIds.includes(this.id) ?? false;
  }

  isLoading(): boolean {
    return false;
  }

  isIntegrated(): boolean {
    return false;
  }
}
